import { ShieldAlert } from 'lucide-react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { SkeletonRegion } from '../../components/SkeletonRegion';
import { TableSkeleton } from '../../components/TableSkeleton';
import { sectionTrail } from '../../layouts/section-trail';
import { buildComplianceOverviewColumns } from './build-compliance-overview-columns';
import { ComplianceOverviewCountTiles } from './ComplianceOverviewCountTiles';
import { ComplianceOverviewTable } from './ComplianceOverviewTable';
import { useComplianceDocumentTypeOptions } from './use-compliance-document-type-options';
import { useComplianceOverview } from './use-compliance-overview';
import { useComplianceOverviewCounts } from './use-compliance-overview-counts';

function CountTilesSkeleton() {
  return (
    <SkeletonRegion label="Loading compliance counts" className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {Array.from({ length: 4 }, (_, i) => (
        <Skeleton key={i} className="h-16 rounded-lg" />
      ))}
    </SkeletonRegion>
  );
}

export function ComplianceOverviewPage() {
  const { state, list, isRefreshing } = useComplianceOverview();
  const countsState = useComplianceOverviewCounts();
  const documentTypeOptions = useComplianceDocumentTypeOptions();

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  const columns = buildComplianceOverviewColumns({ list, documentTypeOptions });

  return (
    <>
      <PageHeader
        breadcrumbs={sectionTrail('compliance')}
        title="Compliance"
        description="Expired, expiring and missing legal documents across the fleet — before dispatch ever sees them."
      />
      {countsState.status === 'loading' && <CountTilesSkeleton />}
      {countsState.status === 'loaded' && <ComplianceOverviewCountTiles counts={countsState.counts} />}
      {state.status === 'error' && (
        <EmptyState
          icon={ShieldAlert}
          title="Couldn't load the compliance overview"
          description="Something went wrong loading this list. Try reloading the page."
        />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading compliance overview" withSearch />}
      {state.status === 'loaded' && <ComplianceOverviewTable page={state.page} columns={columns} list={list} isRefreshing={isRefreshing} />}
    </>
  );
}
